/**
 * The prices on each Flights card (Flights plan Phase 2, 2026-09-12), from the
 * readings site-checker takes each morning. The API returns them grouped by
 * watch and by series; everything here is pure, and the page only renders
 * what it returns.
 *
 * A watch has at most one cash headline and one award headline. An award row
 * can match several programs or flights, so the cheapest live one leads and
 * the rest are counted.
 */

import type { Fields, FlightsDto, Watch } from "./flightsPage";
import { watchEnd, watchStart } from "./flightsPage";
import { localDay as dayOf } from "./localDay";

export type Axis = "cash" | "award";

export interface PricePoint {
  value: number;
  /** When the value was first read. */
  at: string;
  /** The last time site-checker read the same value again, if it has. */
  seen: string | null;
  program: string | null;
  dest: string | null;
  /** The flight priced, when the series joins a pinned watch's flight changes. */
  flight?: string | null;
  /** site-checker emails at this price or below (portal-booked hotels only). */
  alert_at?: number | null;
  /** The minimum behind `alert_at`, in words. */
  alert_rule?: string | null;
}

export interface PriceSeries {
  series: string;
  axis: Axis;
  flight: string | null;
  points: PricePoint[];
}

export interface Quote {
  axis: Axis;
  current: number;
  low: number;
  high: number;
  count: number;
  since: string;
  /** The last time site-checker saw the current number. */
  checked: string;
  atLow: boolean;
  spark: number[];
  program: string | null;
  flight: string | null;
  dest: string | null;
  /** The newest reading's email trigger, when site-checker published one. */
  alertAt: number | null;
  alertRule: string | null;
  /** Other live award options not shown as the headline. */
  others: number;
  /** The retained observations behind the headline, oldest first. */
  history: PricePoint[];
}

const DAY = 86_400_000;

/** An award option unseen this long before the watch's newest award sighting
 * is space seats.aero stopped selling. me-site's /prices uses the same 7 days. */
export const AWARD_LIVENESS_DAYS = 7;

/** A headline this far behind the newest reading on the page missed a run. */
const STALE_MS = 20 * 3_600_000;

const time = (iso: string) => Date.parse(iso);

/** Today's date where the browser is, as YYYY-MM-DD, by localDay.ts's rule. */
export function localDay(d: Date = new Date()): string {
  return dayOf(d.toISOString());
}

function lastSeen(p: PricePoint): string {
  return p.seen && time(p.seen) > time(p.at) ? p.seen : p.at;
}

const last = (s: PriceSeries) => s.points[s.points.length - 1];

/** The values a sparkline draws: consecutive repeats collapse to one point. */
export function movement(values: number[]): number[] {
  return values.filter((v, i) => i === 0 || v !== values[i - 1]);
}

/** Price changes as plotted. A repeat extends the prior point's confirmation
 * window instead of inventing another change at the same value. */
function observedPrices(points: PricePoint[]): PricePoint[] {
  const out: PricePoint[] = [];
  for (const point of points) {
    const prior = out[out.length - 1];
    if (!prior || prior.value !== point.value || (prior.flight ?? null) !== (point.flight ?? null)) {
      out.push({ ...point });
      continue;
    }
    const confirmed = time(lastSeen(point)) > time(lastSeen(prior)) ? lastSeen(point) : lastSeen(prior);
    out[out.length - 1] = { ...prior, seen: confirmed === prior.at ? prior.seen : confirmed };
  }
  return out;
}

function quote(s: PriceSeries, others: number): Quote {
  const history = observedPrices(s.points);
  const values = history.map((p) => p.value);
  const end = history[history.length - 1];
  const low = Math.min(...values);
  return {
    axis: s.axis,
    current: end.value,
    low,
    high: Math.max(...values),
    count: values.length,
    since: history[0].at,
    checked: lastSeen(end),
    atLow: values.length > 1 && end.value <= low,
    spark: values,
    program: end.program,
    flight: s.flight,
    dest: end.dest,
    // From the newest raw reading: a repeat collapsed into an older point
    // keeps that point's fields, and the trigger moves when the paid amount does.
    alertAt: last(s).alert_at ?? null,
    alertRule: last(s).alert_rule ?? null,
    others,
    history,
  };
}

/** The cash headline (the freshest cash series) and the award headline (the
 * cheapest live award series), in that order. Empty when never priced. */
export function quotes(series: PriceSeries[] | undefined): Quote[] {
  const priced = (series ?? []).filter((s) => s.points.length > 0);
  const out: Quote[] = [];
  const cash = priced.filter((s) => s.axis === "cash");
  if (cash.length) {
    out.push(quote(cash.reduce((a, b) => (time(lastSeen(last(b))) > time(lastSeen(last(a))) ? b : a)), 0));
  }
  const award = priced.filter((s) => s.axis === "award");
  if (award.length) {
    const newest = Math.max(...award.map((s) => time(lastSeen(last(s)))));
    const live = award.filter((s) => newest - time(lastSeen(last(s))) <= AWARD_LIVENESS_DAYS * DAY);
    const best = live.reduce((a, b) => (last(b).value < last(a).value ? b : a));
    out.push(quote(best, live.length - 1));
  }
  return out;
}

/** The newest reading anywhere on the page: the last run site-checker made. */
export function newestReading(dto: FlightsDto): number | null {
  let newest: number | null = null;
  for (const series of Object.values(dto.prices ?? {})) {
    for (const s of series) {
      if (!s.points.length) continue;
      const t = time(lastSeen(last(s)));
      if (newest === null || t > newest) newest = t;
    }
  }
  return newest;
}

/** Whether a headline missed the latest run: its number may be out of date. */
export function isStale(q: Quote, newest: number | null): boolean {
  return newest !== null && newest - time(q.checked) > STALE_MS;
}

/** A price as the page shows it: whole dollars or points, rounded UP, the way
 * Google Flights quotes a fare ($363.40 paid is quoted $364). Every comparison
 * rounds through this first, so a gap is the difference of the two numbers on
 * screen. Float noise is trimmed to cents first. Never -0. */
export const whole = (value: number) => Math.ceil(Math.round(value * 100) / 100) || 0;

/** Whole dollars or points, never "-0". */
export function money(axis: Axis, value: number): string {
  const n = whole(value);
  const text = Math.abs(n).toLocaleString("en-US");
  const sign = n < 0 ? "-" : "";
  return axis === "cash" ? `${sign}$${text}` : `${sign}${text} pts`;
}

export interface Mark {
  label: "paid" | "target";
  amount: number;
}

export interface TrackingPart extends Mark {
  axis: Axis;
}

const positive = (v: unknown) => (typeof v === "number" && v > 0 ? v : null);
const recordedAmount = (v: unknown) =>
  (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null);

/** The field holding a watch's baseline on one axis. A hotel has no points. */
function markField(w: Watch, axis: Axis): string | null {
  if (w.kind === "query") return axis === "cash" ? "cash_threshold" : "points_threshold";
  if (axis === "cash") return "price_tracked";
  return w.kind === "flight" ? "points_tracked" : null;
}

/** What a quote is measured against: the price paid for a booking, the
 * target for anything still being shopped. Null when the watch sets none. */
export function markFor(w: Watch, axis: Axis): Mark | null {
  const field = markField(w, axis);
  const amount = field === null ? null : positive(w.fields[field]);
  if (amount === null) return null;
  return { label: w.kind !== "query" && w.fields.booked ? "paid" : "target", amount };
}

/** The PATCH that makes `amount` a drop's new baseline. Points columns are
 * integers and cash keeps cents, both rounded DOWN. */
export function trackAt(drop: PriceDrop, amount: number): Fields {
  const field = markField(drop.watch, drop.mark.axis);
  if (field === null) throw new Error(`No ${drop.mark.axis} baseline on a ${drop.watch.kind}`);
  const scale = drop.mark.axis === "cash" ? 100 : 1;
  return { [field]: Math.floor(amount * scale + 1e-6) / scale };
}

/** The PATCH that makes a drop's current price the new baseline. */
export function trackCurrent(drop: PriceDrop): Fields {
  return trackAt(drop, drop.quote.current);
}

/**
 * The amount typed into the Track dialog, or null when it is not one. Cash
 * takes cents, points must be whole, and both must be positive.
 */
export function parseAmount(axis: Axis, text: string): number | null {
  const clean = text.trim().replace(/[$,]/g, "");
  if (!/^\d+(\.\d{0,2})?$/.test(clean)) return null;
  const n = Number(clean);
  if (!(n > 0) || (axis === "award" && !Number.isInteger(n))) return null;
  return n;
}

/** Every configured baseline that site-checker is actively measuring. */
export function trackingParts(w: Watch): TrackingPart[] {
  return (["cash", "award"] as const).flatMap((axis) => {
    const mark = markFor(w, axis);
    return mark ? [{ ...mark, axis }] : [];
  });
}

/**
 * Whether site-checker is meant to be pricing this watch at all. `watching` is
 * wallet's explicit override and it applies to every kind, so it is read first,
 * exactly as the API's `contract.is_watching` does. Only without one does the
 * kind matter: a search is watched while it is active, a flight or hotel while
 * it carries a baseline.
 */
export function isWatched(w: Watch): boolean {
  if (w.watching != null) return w.watching;
  return w.kind === "query" ? w.fields.active === true : trackingParts(w).length > 0;
}

/** A flight or hotel nobody is pricing: its card says "Not watching". */
export const notWatching = (w: Watch) => (w.kind === "flight" || w.kind === "hotel") && !isWatched(w);

/**
 * The headlines a card shows. None for a flight or hotel nobody is pricing:
 * its last reading predates the decision to stop, so a delta against the
 * current baseline (a rebooked fare, say) would be measured against nothing.
 */
export function shownQuotes(w: Watch, series: PriceSeries[] | undefined): Quote[] {
  return notWatching(w) ? [] : quotes(series);
}

/**
 * A watch site-checker should be pricing that has no reading at all. Only the
 * ones being priced: a watch nobody is pricing already says "Not watching",
 * which is its own explanation.
 */
export function neverPriced(w: Watch, series: PriceSeries[] | undefined): boolean {
  return isWatched(w) && quotes(series).length === 0;
}

/** Shown current minus shown baseline: 0 when the two numbers on screen match. */
const difference = (current: number, amount: number) => whole(current) - whole(amount);

/** Whether a quote is below its baseline, by the same rule the card's verdict
 * uses, so a drop and the card's "Same as paid" cannot disagree. */
const below = (current: number, amount: number) =>
  difference(current, amount) < 0;

export interface PriceDrop {
  watch: Watch;
  quote: Quote;
  mark: TrackingPart;
}

/**
 * Every upcoming travel watch whose current price is below its paid amount or
 * target, the same test site-checker's drop alert applies, so a watch's first
 * reading counts and the page never hides a drop the email reported. A hotel
 * drop above site-checker's published trigger (`alertAt`) is listed too, its
 * verdict flagged, since the email skips it. Largest saving first.
 */
export function activeFares(dto: FlightsDto, today: string): PriceDrop[] {
  const dropRatio = (fare: PriceDrop) => fare.quote.current / fare.mark.amount;
  const newest = newestReading(dto);
  const candidates: PriceDrop[] = [];
  for (const watch of dto.watches) {
    const end = watchEnd(watch);
    if (watch.archived || watch.watching === false
        || (end !== null && end < today)) continue;
    const marks = trackingParts(watch);
    if (!marks.length) continue;
    const byAxis = new Map(quotes(dto.prices?.[watch.watch_id]).map((q) => [q.axis, q]));
    const choices = marks
      .flatMap((mark): PriceDrop[] => {
        const quote = byAxis.get(mark.axis);
        if (!quote || isStale(quote, newest) || !below(quote.current, mark.amount)) return [];
        const observedAt = quote.history[quote.history.length - 1].at;
        if (dto.price_reviews?.[watch.watch_id]?.[mark.axis] === observedAt) return [];
        return [{ watch, mark, quote }];
      })
      .sort((a, b) => dropRatio(a) - dropRatio(b));
    if (choices.length) candidates.push(choices[0]);
  }
  return candidates
    .sort((a, b) =>
      dropRatio(a) - dropRatio(b)
      || (watchEnd(a.watch) ?? "").localeCompare(watchEnd(b.watch) ?? ""));
}

export interface Verdict {
  /** What the row shows: an arrow and the gap, or "Same as paid". */
  text: string;
  /** The same comparison in words, for a screen reader and a hover. */
  label: string;
  /** `floor`: below what you paid, but by less than the booking channel's
   * minimum, so site-checker sends no email for it. */
  tone: "good" | "bad" | "mute" | "floor";
}

/**
 * The price's direction from the baseline as an arrow: "Paid: $38 lower" read
 * as "I paid $38 less". Green means below, never "not worse": paying what you
 * already paid is not news. The tone is also what the "Below tracked price"
 * filter counts, so equal must be muted for the chip to mean what it says.
 * `activeFares` applies the same rule to a drop.
 */
const compared = (mark: "paid" | "target", axis: Axis, diff: number): Verdict => {
  const against = mark === "paid" ? "what you paid" : "your target";
  if (diff === 0) {
    return { text: `Same as ${mark}`, label: `Same as ${against}`, tone: "mute" };
  }
  const gap = money(axis, Math.abs(diff));
  return diff < 0
    ? { text: `▼ ${gap}`, label: `${gap} below ${against}`, tone: "good" }
    : { text: `▲ ${gap}`, label: `${gap} above ${against}`, tone: "bad" };
};

/** Compact comparison to the configured baseline, in shown whole units. A
 * drop above site-checker's published email trigger is flagged, not green. */
export function verdict(q: Quote, mark: Mark | null): Verdict | null {
  if (!mark) return null;
  const diff = difference(q.current, mark.amount);
  const result = compared(mark.label, q.axis, diff);
  if (result.tone !== "good" || q.alertAt === null || q.current <= q.alertAt) return result;
  const rule = q.alertRule ? `under the ${q.alertRule}` : "above the alert price";
  return {
    text: `${result.text} · under min`,
    // In cents: rounded up like the fares, the line could read as already met.
    label: `${result.label}, ${rule} (alerts at $${q.alertAt.toFixed(2)} or less), so no alert`,
    tone: "floor",
  };
}

export interface PaidTotals {
  bookings: number;
  recorded: number;
  cash: number;
  points: number;
  hasCash: boolean;
  hasPoints: boolean;
}

/** Recorded spend for a trip with booked items. `recorded` keeps a
 * partial sum from presenting itself as the trip's complete total. */
export function paidTotals(watches: Watch[]): PaidTotals | null {
  const booked = watches.filter((watch) => Boolean(watch.fields.booked));
  if (booked.length === 0) return null;
  let cash = 0;
  let points = 0;
  let recorded = 0;
  let hasCash = false;
  let hasPoints = false;
  for (const watch of booked) {
    const paidCash = recordedAmount(watch.fields.price_tracked);
    const paidPoints = watch.kind === "flight" ? recordedAmount(watch.fields.points_tracked) : null;
    if (paidCash !== null || paidPoints !== null) recorded += 1;
    if (paidCash !== null) hasCash = true;
    if (paidPoints !== null) hasPoints = true;
    // Sum what each row shows, so the total adds up on screen.
    cash += whole(paidCash ?? 0);
    points += whole(paidPoints ?? 0);
  }
  return recorded > 0
    ? { bookings: booked.length, recorded, cash, points, hasCash, hasPoints }
    : null;
}

export interface SerpEstimate {
  daily: number;
  monthly: number;
}

/** Metered cash searches requested each day, multiplied by 365 / 12.
 * Fixed round trips search both directions per destination. A pinned flight
 * also needs its airline-direct fare-options lookup. A watch stops counting the
 * day after its `searchDay`, so the total falls on its own as trips pass. */
export function serpEstimate(watches: Watch[], today: string): SerpEstimate {
  const daily = watches.reduce((total, watch) => {
    if (watch.archived) return total;
    // site-checker stops searching once a watch's start day has passed.
    const day = watchStart(watch);
    if (day !== null && day < today) return total;
    const f = watch.fields;
    const cash = positive(watch.kind === "query" ? f.cash_threshold : f.price_tracked);
    if (!isWatched(watch) || cash === null) return total;
    if (watch.kind === "query") {
      if (f.min_nights != null) return total;
      return total + 2 * (Array.isArray(f.destinations) ? f.destinations.length : 0);
    }
    return total + (watch.kind === "flight" && f.flight ? 2 : 1);
  }, 0);
  return { daily, monthly: Math.round((daily * 365) / 12) };
}

/** One leg of a flexible search's round trip, as site-checker found it. */
export interface OptionLeg {
  date: string;
  flight_number: string | null;
  depart_local: string;
  arrive_local: string;
  miles: number;
  program: string | null;
  taxes: number | null;
}

export interface OptionPair {
  out: OptionLeg;
  ret: OptionLeg;
  nights: number;
  total_miles: number;
  total_taxes: number;
}

/** A flexible search's best round trips from site-checker's last run,
 * cheapest first. Empty pairs means it looked and found no award space. */
export interface Options {
  as_of: string;
  pairs: OptionPair[];
}

/** One way of a cash round trip, as site-checker found it. */
export interface CashLeg {
  date: string;
  flight: string;
  departs: string | null;
  arrives: string | null;
  price: number;
  stops: number;
}

/** A round trip on set dates: the cheapest cash pairing to one destination. */
export interface CashCombo {
  dest: string;
  price: number;
  out: CashLeg;
  ret: CashLeg;
}

/** A round trip's cash pairings from the last run, cheapest first. Empty
 * pairs means it looked and no fare matched the search. */
export interface CashOptions {
  as_of: string;
  pairs: CashCombo[];
}

/** A search's latest round trips, by axis. */
export interface WatchOptions {
  award?: Options;
  cash?: CashOptions;
}
